import { useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { lt, showSuccessAlert, validateWithZod } from '@mews/framework';
import { RouteName } from '@mews-commander/enums';
import { getCurrentEnterpriseDefaultLanguageCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { commanderForm } from '@mews-commander/legacy-form';
import { useRateGroupAddMutation } from '../useRateGroupAddMutation';
import {
    RateGroupCreateFormModel,
    RateGroupForm,
    RateGroupFormProps,
    createRateGroupCreateFormModel,
    createRateGroupCreateFormValidator,
} from '../RateGroupForm';
import { showRouteInModal } from '../../app/actions/common';
import { useInitialValues } from './useInitialValues';

const ReduxRateGroupForm = pipe(
    RateGroupForm,
    commanderForm<RateGroupCreateFormModel, RateGroupFormProps>({
        submitLabel: lt('Save'),
        submitButtonProps: {
            variant: 'primary',
        },
    })
);

export interface RateGroupFormCreateProps {
    serviceId: string;
    form: string;
}

export const RateGroupCreateForm = ({ serviceId, ...props }: RateGroupFormCreateProps) => {
    const { mutateAsync } = useRateGroupAddMutation();

    const dispatch = useCommanderThunkDispatch();
    const initialValues = useInitialValues(serviceId);

    const defaultLanguageCode = useCommanderSelector<string>(getCurrentEnterpriseDefaultLanguageCode);
    const validateSchema = useMemo(
        () =>
            pipe(
                { languageCode: defaultLanguageCode },
                createRateGroupCreateFormModel,
                createRateGroupCreateFormValidator
            ),
        [defaultLanguageCode]
    );

    const handleSubmit = async (data: RateGroupCreateFormModel) => {
        /**
         * In react-hook-form, if this zod transform is used as a validate schema,
         * then the `mutate` could be used directly as a submit function.
         */
        const params = validateSchema.parse(data);
        return mutateAsync(params, {
            onSuccess: (result) => {
                showSuccessAlert({ description: lt('RateGroupCreatedMessage') });
                dispatch(
                    showRouteInModal(RouteName.SettlementRuleIndex, { query: { OwnerId: result.rateGroups[0]?.id } })
                );
            },
            // eslint-disable-next-line @mews/no-empty-catch
        }).catch(() => {
            // The error is handled by redux-form
        });
    };

    return (
        <ReduxRateGroupForm
            {...props}
            defaultLanguageCode={defaultLanguageCode}
            serviceId={serviceId}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validate={validateWithZod(validateSchema)}
        />
    );
};
